import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchPublicBusinessProfile } from '@/utils/publicBusinessApi';
import { toast } from 'sonner';
import { 
  type BusinessData,
  transformBusinessDataFromState,
  transformBusinessDataFromApi
} from '@/components/public/adventures/utils/customAdventureUtils';

export const useBusinessData = (businessId: string | undefined) => {
  const location = useLocation();
  const { t } = useTranslation('public', { keyPrefix: 'customAdventures' });
  const [businessData, setBusinessData] = useState<BusinessData | null>(null);
  const [businessLoading, setBusinessLoading] = useState(true);

  useEffect(() => {
    const loadBusinessData = async () => {
      try {
        setBusinessLoading(true);
        
        // Try to get business data from navigation state first
        const stateData = location.state as {
          businessName: string;
          businessImage: string | null;
          businessDescription: string;
          businessLocation: any;
          businessEmoji: string;
        } | null;

        if (stateData) {
          setBusinessData(transformBusinessDataFromState(stateData, businessId || ''));
          setBusinessLoading(false);
        } else if (businessId) {
          // If no state data, fetch from API using public endpoint
          const response = await fetchPublicBusinessProfile(businessId);
          if (response) {
            setBusinessData(transformBusinessDataFromApi(response));
          } else {
            throw new Error('Invalid business data received');
          }
          setBusinessLoading(false);
        } else {
          throw new Error('No business ID provided');
        }
      } catch (error) {
        console.error('Error loading business data:', error);
        toast.error(t('business.error.description'));
        setBusinessLoading(false);
      }
    };

    loadBusinessData();
  }, [businessId, location.state, t]);

  return { businessData, businessLoading };
};
